import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { gray } from "../../theme/themePrimitives";

interface Item {
  id: string | number;
  title: string;
  subtitle?: string;
}

interface SelectedItemsPreviewProps {
  items: Item[];
  itemType: "users" | "assignments" | "emails";
  onViewDetails?: (items: Item[]) => void;
  previewTitle?: string;
  previewDescription?: string;
  dialogTitle?: string;
  showViewDetailsButton?: boolean;
}

const SelectedItemsPreview: React.FC<SelectedItemsPreviewProps> = ({
  items = [],
  itemType,
  onViewDetails,
  previewTitle,
  previewDescription,
  dialogTitle,
  showViewDetailsButton = true,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const getDefaultPreviewTitle = () => {
    switch (itemType) {
      case "users":
        return `${items.length} ${items.length === 1 ? "User" : "Users"}`;
      case "assignments":
        return `${items.length} ${items.length === 1 ? "Assignment" : "Assignments"}`;
      case "emails":
        return `${items.length} ${items.length === 1 ? "Recipient" : "Recipients"}`;
      default:
        return `${items.length} Items`;
    }
  };

  const getDefaultPreviewDescription = () => {
    switch (itemType) {
      case "users":
        return "View and manage the users this change will affect";
      case "assignments":
        return "View and manage the assignments this change will affect";
      case "emails":
        return "View and manage the users this email will be sent to";
      default:
        return "View and manage the selected items";
    }
  };

  const getDefaultDialogTitle = () => {
    return (
      dialogTitle ||
      `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} Details`
    );
  };

  const getIcon = () => {
    switch (itemType) {
      case "users":
        return PersonOutlineIcon;
      case "assignments":
        return AssignmentOutlinedIcon;
      case "emails":
        return EmailIcon;
      default:
        return PersonOutlineIcon;
    }
  };

  const getButtonText = () => {
    switch (itemType) {
      case "users":
        return "View details";
      case "assignments":
        return "Show assignments";
      case "emails":
        return "View details";
      default:
        return "View details";
    }
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(items);
    } else {
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const IconComponent = getIcon();

  return (
    <>
      <Paper
        sx={{
          mb: 2,
          // backgroundColor: 'white',
          backgroundColor: itemType === "assignments" ? "#FCFAFF" : "white",
          position: "relative",
          ...(itemType === "assignments" && {
            borderLeft: "4px solid #7F56D9",
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 50,
                backgroundColor:
                  itemType === "assignments" ? "transparent" : gray[100],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconComponent sx={{ color: "#7F56D9" }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {previewTitle || getDefaultPreviewTitle()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {previewDescription || getDefaultPreviewDescription()}
              </Typography>
            </Box>
          </Box>
          {showViewDetailsButton && (
            <Button
              variant="outlined"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={handleViewDetails}
              sx={{
                color: "#6D28D9",
                border: "1px solid  #D6BBFB",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "12px",
                "&:hover": {
                  borderColor: "#5B21B6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              {getButtonText()}
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          {getDefaultDialogTitle()}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 3 }}>
          <Stack spacing={2}>
            {items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "6px",
                    backgroundColor: gray[100],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconComponent sx={{ color: "#6B7280", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: "14px" }}>
                    {item.subtitle}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelectedItemsPreview;
